/** AI import from URL — `/v2/console/ops/ai_import/*` (perm console_bg_ai_import). */
import { apiGet, apiPost, client } from './client';

export interface AiImportModelOption {
  id: string;
  label: string;
  default: boolean;
  input_cost_per_mtok: number;
  output_cost_per_mtok: number;
}

export interface AiImportRestechAccount {
  integration: string;
  account: string | null;
  display_name: string;
  supports_product_list: boolean;
}

export interface AiImportLaunchContext {
  models: AiImportModelOption[];
  caps: {
    max_urls: number;
    max_seed_urls: number;
    max_pages_default: number;
    max_pages_cap: number;
    max_cost_usd_default: number;
    max_cost_usd_cap: number;
    max_workers_cap: number;
  };
  restech: {
    has_booking_integration: boolean;
    accounts: AiImportRestechAccount[];
  } | null;
  /** Whether the Google Places enhancement can run (a key is configured). */
  google_places: {
    available: boolean;
    default_enabled: boolean;
  };
  /** Document-upload limits + the accepted extensions for the dropzone. */
  uploads: {
    max_files: number;
    max_mb: number;
    accept: string[];
  };
}

export type AiImportMode = 'activity' | 'itinerary' | 'tour' | 'catalog';

/** A staged upload (from the upload endpoint), referenced by `start`. */
export interface AiImportDocumentInput {
  file_key: string;
  filename: string;
  content_type?: string;
}

export interface AiImportStartInput {
  tour_operator_id: number;
  mode: AiImportMode;
  urls: string[];
  model?: string | null;
  max_pages?: number;
  /** Catalog crawl depth. null/omitted = unlimited; 0 = seed URLs only; N levels. */
  max_depth?: number | null;
  /** Parallel page fetchers. null/omitted/1 = sequential. */
  max_workers?: number | null;
  budget_usd?: number;
  dry_run?: boolean;
  /** Booking-system catalog is authoritative; matched drafts adopt product ids. */
  restech_primary?: boolean;
  restech_integration?: string;
  /** Account slugs (null = default account). Omit = all configured accounts. */
  restech_accounts?: (string | null)[];
  /** Uploaded documents (keys from the upload endpoint) to extract with urls. */
  documents?: AiImportDocumentInput[];
  /** Opt out of the post-extraction Google Places matching + enrichment. */
  skip_google_places?: boolean;
}

export interface StartTaskResult {
  id: string;
  state: number;
  tour_operator_id: number;
}

export type AiDraftKind = 'activity' | 'itinerary' | 'tour';

/** Set on tour drafts bound to a booking-system product (restech-primary). */
export interface AiDraftRestechMatch {
  integration: string;
  account: string | null;
  product_id: string;
  product_name: string;
  /** Name-similarity score (1 = exact). */
  score: number;
  /** existing_mapping = matched through a tour already bound to the product. */
  via: 'existing_mapping' | 'name';
  tour_id: number | null;
}

export interface AiDraftSummary {
  ext_id: string;
  kind: AiDraftKind;
  name: string;
  source_url: string;
  confidence: number | null;
  issues: string[];
  photo_count: number;
  day_count: number | null;
  activity_count: number | null;
  restech_match: AiDraftRestechMatch | null;
  selected_default: boolean;
  /** A record with this ext_id already exists for the tenant → import updates it. */
  exists: boolean;
}

export interface AiDraftPhoto {
  url: string;
  caption: string | null;
}

export interface AiDraftDetail extends Omit<AiDraftSummary, 'photo_count' | 'day_count' | 'activity_count'> {
  fields: Record<string, unknown>;
  images: AiDraftPhoto[];
}

export interface AiImportRestechSummary {
  integration: string;
  products_total: number;
  matched: number;
  web_only: number;
  unmatched_products: {
    integration: string;
    account: string | null;
    id: string;
    name: string;
  }[];
}

export interface AiImportDraftsSummary {
  total: number;
  by_kind: Record<string, number>;
  pages_crawled: number;
  extraction_cost_usd: number;
  warnings_count: number;
  partial: boolean;
  partial_reason: 'budget_cap' | 'page_cap' | 'record_cap' | 'crawl_error' | 'blocked' | null;
  restech: AiImportRestechSummary | null;
}

export interface AiImportDraftsPage {
  task_id: string;
  task_state: number;
  summary: AiImportDraftsSummary;
  /** False when the task opted out of Places or no key is configured. */
  google_places_enabled: boolean;
  /** The task's uploaded input documents (drafts from one carry its `source_url`). */
  documents: AiImportDocument[];
  count: number;
  page: number;
  page_size: number;
  results: AiDraftSummary[];
}

export interface AiImportListDraftsParams {
  page?: number;
  page_size?: number;
  kind?: AiDraftKind;
  search?: string;
  min_confidence?: number;
}

/** Approval payload for POST /bgtasks/{id}/approve/ on ai_import tasks.
 *  `place_selections` maps an activity ext_id -> chosen Google place id. */
export type AiImportApproveSelection = (
  | { selected_ids: string[] }
  | { select_all: true; excluded_ids?: string[] }
) & { place_selections?: Record<string, string> };

/** One Google Place candidate for an activity (from Text Search). */
export interface AiPlaceCandidate {
  place_id: string;
  name: string | null;
  formatted_address: string | null;
  location: { lat: number | null; lng: number | null } | null;
}

/** An uploaded input document of the task. */
export interface AiImportDocument {
  /** Position in the task's documents — addresses `documentUrl`. */
  index: number;
  filename: string;
  /** `upload://<filename>` — the `source_url` of the drafts extracted from it. */
  source_url: string;
}

/** A geographic context: one tour (its embedded activities) or the standalone set. */
export interface AiPlaceGroup {
  id: string;
  kind: 'tour' | 'standalone';
  ext_id: string | null;
  name: string;
}

/** A geographic area the AI geo pass found for a group; lookups are restricted to it. */
export interface AiPlaceArea {
  id: string;
  group_id: string;
  label: string;
  country_codes: string[];
  center: { lat: number; lng: number };
  radius_km: number;
}

/** An activity (standalone or tour-embedded) with its area and in-area candidates. */
export interface AiPlaceActivity {
  key: string;
  name: string;
  /** Null on tasks staged before the geo pass existed. */
  group_id: string | null;
  /** Null when the geo pass could not place it (no automatic lookup then). */
  area_id: string | null;
  query: string;
  /** False for lines that are not a findable place — no automatic lookup. */
  is_place: boolean;
  candidates: AiPlaceCandidate[];
  /** The first candidate — the default pick. */
  suggested_place_id: string | null;
}

export interface AiImportPlaceSuggestions {
  google_places_enabled: boolean;
  groups: AiPlaceGroup[];
  areas: AiPlaceArea[];
  activities: AiPlaceActivity[];
  /** True when more activities exist than the per-run suggestion cap. */
  truncated: boolean;
}

/** Candidates for one activity re-looked-up inside another area of its tour. */
export interface AiImportPlaceCandidates {
  key: string;
  area_id: string;
  candidates: AiPlaceCandidate[];
  suggested_place_id: string | null;
}

/** A staged upload returned by the upload endpoint. */
export interface AiImportUploadFile {
  file_key: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface AiImportResultEntry {
  ext_id: string;
  kind: string;
  record_id: number;
  name: string;
}

export interface AiImportResult {
  imported: AiImportResultEntry[];
  skipped: number;
  failed: { ext_id: string; kind: string; error: string }[];
  dry_run: boolean;
  cost_usd: number;
}

export async function launchContext(toId?: number): Promise<AiImportLaunchContext> {
  const qs = toId ? `?tour_operator_id=${toId}` : '';
  return apiGet(`/ops/ai_import/launch_context/${qs}`);
}

export async function start(body: AiImportStartInput): Promise<StartTaskResult> {
  return apiPost('/ops/ai_import/start/', body);
}

export async function listDrafts(
  taskId: string,
  params: AiImportListDraftsParams = {},
): Promise<AiImportDraftsPage> {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.page_size) usp.set('page_size', String(params.page_size));
  if (params.kind) usp.set('kind', params.kind);
  if (params.search) usp.set('search', params.search);
  if (params.min_confidence !== undefined) usp.set('min_confidence', String(params.min_confidence));
  const qs = usp.toString();
  return apiGet(`/ops/ai_import/${taskId}/drafts/${qs ? `?${qs}` : ''}`);
}

export async function getDraft(taskId: string, extId: string): Promise<AiDraftDetail> {
  return apiGet(`/ops/ai_import/${taskId}/drafts/${encodeURIComponent(extId)}/`);
}

/** POST /ops/ai_import/upload/ — multipart upload of one brochure/document,
 *  staged to S3. The returned file_key is referenced in `start`. */
export async function upload(file: File): Promise<AiImportUploadFile> {
  const fd = new FormData();
  fd.append('file', file);
  // Let axios set the multipart boundary itself.
  const { data } = await client.post<{ files: AiImportUploadFile[] }>(
    '/ops/ai_import/upload/',
    fd,
    { headers: { 'Content-Type': undefined } },
  );
  const first = data.files[0];
  if (!first) throw new Error('Upload returned no file.');
  return first;
}

/** POST /ops/ai_import/{taskId}/place_suggestions/ — every activity's geographic
 *  area and the Google Place candidates found inside it (computed by the extract
 *  step; `suggested_place_id` is the default pick). */
export async function placeSuggestions(taskId: string): Promise<AiImportPlaceSuggestions> {
  return apiPost(`/ops/ai_import/${taskId}/place_suggestions/`, {});
}

/** POST /ops/ai_import/{taskId}/place_candidates/ — re-run one activity's lookup
 *  inside another area of its tour (the reviewer changed its area). */
export async function placeCandidates(
  taskId: string,
  key: string,
  areaId: string,
): Promise<AiImportPlaceCandidates> {
  return apiPost(`/ops/ai_import/${taskId}/place_candidates/`, { key, area_id: areaId });
}

/** GET /ops/ai_import/{taskId}/place_search/?q=[&area_id=] — free-text place
 *  search for changing a suggested match; `areaId` biases (never restricts) it. */
export async function placeSearch(
  taskId: string,
  q: string,
  signal?: AbortSignal,
  areaId?: string | null,
): Promise<{ candidates: AiPlaceCandidate[] }> {
  const usp = new URLSearchParams({ q });
  if (areaId) usp.set('area_id', areaId);
  return apiGet(
    `/ops/ai_import/${taskId}/place_search/?${usp.toString()}`,
    signal ? { signal } : undefined,
  );
}

/** GET /ops/ai_import/{taskId}/documents/{index}/url/ — presigned download link
 *  for one of the task's uploaded input documents (410 once expired). */
export async function documentUrl(
  taskId: string,
  index: number,
): Promise<{ url: string; filename: string; expires_in: number }> {
  return apiGet(`/ops/ai_import/${taskId}/documents/${index}/url/`);
}
